import { useUpdateSurfacePreferences } from "@/components/preferences/usePreferences.tsx";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useVisibleCalendarIds } from "./useVisibleCalendarIds.ts";

/**
 * Toggles one calendar's visibility, returning the next visible-id list. When
 * nothing is explicitly hidden yet (`visibleCalendarIds` undefined) every
 * calendar is implicitly visible, so the first hide must materialize the full
 * set before removing the one being toggled off.
 */
export function computeToggledVisibleCalendarIds(
  calendars: readonly { calendarId: string }[],
  visibleCalendarIds: string[] | undefined,
  calendarId: string,
): string[] {
  const currentVisible = visibleCalendarIds
    ? new Set(visibleCalendarIds)
    : new Set(calendars.map((c) => c.calendarId));
  if (currentVisible.has(calendarId)) {
    currentVisible.delete(calendarId);
  } else {
    currentVisible.add(calendarId);
  }
  return [...currentVisible];
}

/**
 * Calendar visibility state + toggle, shared by the desktop aside
 * (CalendarNavPanel) and the mobile calendar-options sheet so both surfaces
 * write the same server preference and refresh the same events query.
 */
export function useCalendarVisibilityToggle(): {
  hiddenCalendarIds: Set<string>;
  toggleCalendar: (calendarId: string) => void;
} {
  const queryClient = useQueryClient();
  const updateCalendarPrefs = useUpdateSurfacePreferences("calendars");
  const { calendars, visibleCalendarIds, hiddenCalendarIds } = useVisibleCalendarIds();

  const toggleCalendar = useCallback(
    (calendarId: string) => {
      if (!calendars) return;
      const newVisible = computeToggledVisibleCalendarIds(
        calendars,
        visibleCalendarIds,
        calendarId,
      );
      updateCalendarPrefs({
        visibleCalendarIds: JSON.stringify(newVisible),
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ["calendarEvents"] });
      });
    },
    [calendars, visibleCalendarIds, updateCalendarPrefs, queryClient],
  );

  return { hiddenCalendarIds, toggleCalendar };
}
